import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import ConfirmModal from '../components/ui/ConfirmModal';

const UIContext = createContext();

export const useUI = () => {
    const context = useContext(UIContext);
    if (!context) {
        throw new Error('useUI must be used within a UIProvider');
    }
    return context;
};

export const UIProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);
    const [modal, setModal] = useState(null); // { title: string, content: ReactNode, onClose: function }
    const [confirm, setConfirm] = useState(null); // { title, message, onConfirm, type }

    const [theme, setTheme] = useState(() => {
        const saved = localStorage.getItem('theme');
        if (saved) return saved;
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    });

    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
        return localStorage.getItem('sidebar_collapsed') === 'true';
    });

    useEffect(() => {
        if (theme === 'dark') {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }
    }, [theme]);

    const toggleTheme = useCallback(() => {
        setTheme(prev => {
            const next = prev === 'dark' ? 'light' : 'dark';
            localStorage.setItem('theme', next);
            return next;
        });
    }, []);

    const toggleSidebar = useCallback(() => {
        setIsSidebarCollapsed(prev => {
            const next = !prev;
            localStorage.setItem('sidebar_collapsed', next);
            return next;
        });
    }, []);

    const showToast = useCallback((message, type = 'info', duration = 3000) => {
        const id = Date.now();
        setToasts((prev) => [...prev, { id, message, type }]);
        setTimeout(() => {
            setToasts((prev) => prev.filter((t) => t.id !== id));
        }, duration);
    }, []);

    const hideToast = useCallback((id) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);

    const showModal = useCallback((modalData) => {
        setModal(modalData);
    }, []);

    const hideModal = useCallback(() => {
        setModal(null);
    }, []);

    const showConfirm = useCallback((confirmData) => {
        setConfirm(confirmData);
    }, []);

    const hideConfirm = useCallback(() => {
        setConfirm(null);
    }, []);

    const handleConfirmAction = () => {
        if (confirm?.onConfirm) confirm.onConfirm();
        hideConfirm();
    };

    const handleCancelAction = () => {
        if (confirm?.onCancel) confirm.onCancel();
        hideConfirm();
    };

    return (
        <UIContext.Provider value={{
            showToast, hideToast, showModal, hideModal, showConfirm, hideConfirm,
            theme, toggleTheme,
            isSidebarCollapsed, toggleSidebar
        }}>
            {children}
            {/* Toast Container */}
            <div className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2">

                {toasts.map((toast) => (
                    <Toast key={toast.id} {...toast} onClose={() => hideToast(toast.id)} />
                ))}
            </div>
            {/* Modal Container */}
            {modal && <Modal {...modal} onClose={hideModal} />}
            {/* Confirm Modal */}
            {confirm && (
                <ConfirmModal
                    isOpen={true}
                    {...confirm}
                    onConfirm={handleConfirmAction}
                    onCancel={handleCancelAction}
                />
            )}
        </UIContext.Provider>
    );
};

// Internal Components
const Toast = ({ message, type, onClose }) => {
    const config = {
        success: {
            border: 'border-l-emerald-500',
            icon: 'check_circle',
            iconColor: 'text-emerald-500',
            bg: 'bg-emerald-50 dark:bg-emerald-900/20',
        },
        error: {
            border: 'border-l-rose-500',
            icon: 'error',
            iconColor: 'text-rose-500',
            bg: 'bg-rose-50 dark:bg-rose-900/20',
        },
        warning: {
            border: 'border-l-amber-500',
            icon: 'warning',
            iconColor: 'text-amber-500',
            bg: 'bg-amber-50 dark:bg-amber-900/20',
        },
        info: {
            border: 'border-l-blue-500',
            icon: 'info',
            iconColor: 'text-blue-500',
            bg: 'bg-blue-50 dark:bg-blue-900/20',
        },
    }[type] || {
        border: 'border-l-slate-400',
        icon: 'info',
        iconColor: 'text-slate-400',
        bg: 'bg-slate-50 dark:bg-slate-800',
    };

    return (
        <div className={`group flex items-start gap-4 pl-4 pr-4 py-4 rounded-2xl shadow-2xl border border-slate-200/50 dark:border-slate-700/50 border-l-4 ${config.border} bg-white/90 dark:bg-slate-900/90 backdrop-blur-md min-w-[320px] max-w-[450px] animate-in slide-in-from-right-full duration-300`}>
            <div className={`shrink-0 size-10 rounded-xl flex items-center justify-center ${config.bg} shadow-sm border border-black/5 dark:border-white/5`}>
                <span className={`material-symbols-outlined text-[24px] ${config.iconColor}`}>{config.icon}</span>
            </div>
            <div className="flex-1 pt-0.5">
                <p className="text-sm font-bold text-slate-800 dark:text-slate-100 leading-tight">{message}</p>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1 opacity-60">{type}</p>
            </div>
            <button
                onClick={onClose}
                className="shrink-0 size-8 flex items-center justify-center rounded-xl text-slate-300 hover:text-slate-900 dark:text-slate-600 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-all opacity-0 group-hover:opacity-100"
            >
                <span className="material-symbols-outlined text-[18px]">close</span>
            </button>
        </div>
    );
};

const Modal = ({ title, content, onClose }) => {
    return (
        <div className="fixed inset-0 z-[6000] flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-300"
                onClick={onClose}
            ></div>

            {/* Modal Content */}
            <div className="relative w-full max-w-lg bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl shadow-primary/20 border border-slate-200 dark:border-slate-800 overflow-hidden animate-in zoom-in-95 duration-500">
                {/* Premium Gradient Header */}
                <div className="shrink-0 px-8 py-6 relative overflow-hidden bg-gradient-to-br from-primary/10 via-primary/5 to-transparent dark:from-primary/20 dark:via-primary/10 dark:to-transparent border-b border-primary/10 dark:border-primary/20">
                    <div className="relative z-10 flex justify-between items-center">
                        <div className="flex gap-4 items-center">
                            <div className="size-10 rounded-xl bg-white dark:bg-slate-800 flex items-center justify-center shadow-sm border border-primary/10">
                                <span className="material-symbols-outlined text-primary text-[24px] font-bold">bubble_chart</span>
                            </div>
                            <h3 className="text-lg font-black text-slate-900 dark:text-white leading-tight uppercase tracking-wider">{title}</h3>
                        </div>
                        <button
                            onClick={onClose}
                            className="size-10 flex items-center justify-center rounded-xl text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-white dark:hover:bg-slate-800 shadow-sm hover:shadow transition-all"
                        >
                            <span className="material-symbols-outlined">close</span>
                        </button>
                    </div>
                </div>

                <div className="p-8 text-slate-600 dark:text-slate-400 max-h-[70vh] overflow-y-auto custom-scrollbar">
                    {content}
                </div>
            </div>
        </div>
    );
};
